import * as React from 'react'
import { logWarnOnce } from '@ds/logging'
import { isIE, keyboardNavigableElements } from '../../Utility'

/*
    Given a `root` container element and a `target` that is outside of that
    container and intended to receive focus, force the DOM focus to wrap around
    such that it remains within `root`.

    If `forceFirst` is  to `true`, the wrap will always attempt to focus the
    first viable element in `root`, rather than wrapping to the end.

    If no navigables exist in the `root` then put focus on the first
    focus fence.
*/
export function moveFocusToContainer(
  trapId: string,
  root: HTMLElement,
  moveFocus: (element: HTMLElement) => void,
  target: Element,
  forceFirst = false
) {
  const navigables = new Set<Element>(keyboardNavigableElements(root))
  if (navigables.size === 0) {
    logWarnOnce(
      'Warning: attempting to trap navigation but the container does not have at least 1 navigable item.'
    )
  }

  const acceptNode = (node: HTMLElement) => {
    return !isFence(node) &&
      navigables.has(node) &&
      !(node as HTMLFormElement).disabled
      ? NodeFilter.FILTER_ACCEPT
      : NodeFilter.FILTER_SKIP
  }

  const walker = isIE()
    ? document.createTreeWalker(
        root,
        NodeFilter.SHOW_ELEMENT,
        acceptNode,
        // @ts-expect-error because acceptNode is a unique paramter, IE takes 4 arguments as opposed to the type def for createTreeWalker which expects 1-3
        false
      )
    : document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT, {
        acceptNode,
      })

  const position = target.compareDocumentPosition(root)
  let wrappedTarget: HTMLElement | null = null

  // eslint-disable-next-line no-bitwise
  if (position & Node.DOCUMENT_POSITION_PRECEDING || forceFirst) {
    wrappedTarget = walker.firstChild() as HTMLElement | null
    // eslint-disable-next-line no-bitwise
  } else if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
    wrappedTarget = walker.lastChild() as HTMLElement | null
  }
  const newFocus = (wrappedTarget || firstFence(trapId)) ?? root
  moveFocus(newFocus)
}

export function focusInitial(
  trapId: string,
  trapContainer: HTMLElement,
  moveFocus: (element: HTMLElement) => void,
  initialElement: string | HTMLElement | undefined
) {
  let customFocusEl: HTMLElement | undefined
  if (initialElement) {
    if (typeof initialElement === 'string') {
      customFocusEl =
        (trapContainer.querySelector(
          initialElement as string
        ) as HTMLElement) ?? undefined
    } else {
      customFocusEl = initialElement
    }
  }
  if (customFocusEl && isInsideTrap(trapContainer, customFocusEl)) {
    moveFocus(customFocusEl)
  } else if (
    !isInsideTrap(trapContainer, document.activeElement) &&
    trapContainer.querySelector('[autofocus]') === null
  ) {
    moveFocusToContainer(
      trapId,
      trapContainer,
      moveFocus,
      document.activeElement as HTMLElement,
      true
    )
  }
}

/*
    Fence
*/

const FOCUS_FENCE_ATTRIBUTE_NAME = 'data-focus-fence'

interface FenceProps {
  /** Unique id of the the trap */
  trapId: string
  /** Focus handler */
  onFocus: (event: React.FocusEvent) => void
  /** Blur handler */
  onBlur: (event: React.FocusEvent) => void
  /** data-qa */
  qa: string
}

/**
 * A "bookend" for the navigation trap. Ensures that focus cannot leave trap container.
 */
export const Fence = ({ trapId, onFocus, onBlur, qa }: FenceProps) => (
  <div
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    tabIndex={0}
    style={{ position: 'fixed', opacity: 0, pointerEvents: 'none' }}
    {...{
      [FOCUS_FENCE_ATTRIBUTE_NAME]: trapId,
    }}
    onFocus={onFocus}
    onBlur={onBlur}
    data-qa={qa}
  />
)

export function isFence(element: Element, trapId?: string) {
  const fenceTrapId = element.getAttribute(FOCUS_FENCE_ATTRIBUTE_NAME)
  return trapId ? fenceTrapId === trapId : fenceTrapId !== null
}

function firstFence(trapId: string) {
  return document.querySelector(
    `[${FOCUS_FENCE_ATTRIBUTE_NAME}="${trapId}"]`
  ) as HTMLElement
}

function isInsideTrap(trapContainer: HTMLElement, element?: Element | null) {
  return trapContainer.contains(element ?? null)
}
